// ============================================================================
// apps/web/src/lib/worker.ts
// ----------------------------------------------------------------------------
// Client typé pour le Worker Magic (apps/worker). Ajoute automatiquement
// le bearer token (JWT Supabase) à chaque requête. Centralise les types
// de payload / réponse pour les routes admin.
// ============================================================================

import { supabase } from './supabase';
import type { CapabilityKey, RoleKey } from '@magic/shared';

const WORKER_URL = import.meta.env.VITE_WORKER_URL ?? 'http://localhost:8787';

async function authHeader(): Promise<HeadersInit> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function callWorker<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(await authHeader()),
  };
  const res = await fetch(`${WORKER_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    let payload: unknown;
    try { payload = JSON.parse(text); } catch { payload = text; }
    throw new WorkerError(res.status, payload);
  }
  return res.json() as Promise<T>;
}

export class WorkerError extends Error {
  constructor(public status: number, public payload: unknown) {
    super(`Worker error ${status}`);
  }
}

// ─── /api/me ───────────────────────────────────────────────────────────────

export type MeResponse = {
  profile: {
    id: string;
    role: RoleKey;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    display_color: string | null;
    must_change_password: boolean;
    is_supervisor: boolean;
    extra_capabilities: string[];
    is_active: boolean;
  };
  capabilities: string[];
  locations: { location_id: string; is_primary: boolean }[];
};

export function fetchMe() {
  return callWorker<MeResponse>('GET', '/api/me');
}

// ─── /api/admin/users ──────────────────────────────────────────────────────

export type CreateStaffPayload = {
  email: string;
  first_name: string;
  last_name: string;
  role: RoleKey;
  contract_type?: 'variable' | 'fixed_39' | 'fixed_35' | null;
  contract_hours_per_week?: number | null;
  evening_surcharge_enabled?: boolean;
  sunday_surcharge_enabled?: boolean;
  is_supervisor?: boolean;
  cycle_weeks?: number;
  hire_date?: string | null;
  location_ids?: string[];
  primary_location_id?: string | null;
  extra_capabilities?: CapabilityKey[];
};

export type CreateStaffResponse = {
  user_id: string;
  email: string;
  must_change_password: true;
  // Le mot de passe temporaire est désormais ENVOYÉ PAR EMAIL au staff.
  // `one_time_password` n'est renvoyé QUE si l'email a échoué (repli UI).
  email_sent: boolean;
  one_time_password?: string;
  email_error?: string;
};

export function createStaff(payload: CreateStaffPayload) {
  return callWorker<CreateStaffResponse>('POST', '/api/admin/users', payload);
}

export type ResetPasswordResponse = {
  user_id: string;
  must_change_password: true;
  email_sent: boolean;
  one_time_password?: string;
  email_error?: string;
};

export function resetUserPassword(userId: string) {
  return callWorker<ResetPasswordResponse>(
    'POST',
    `/api/admin/users/${userId}/reset-password`,
  );
}

// Change l'email de connexion (Supabase Auth) + le miroir user_profiles.email.
export function updateUserEmail(userId: string, email: string) {
  return callWorker<{ user_id: string; email: string }>(
    'POST',
    `/api/admin/users/${userId}/email`,
    { email },
  );
}

export function updateUserRole(userId: string, role: RoleKey) {
  return callWorker<{ user_id: string; role: RoleKey }>(
    'PATCH',
    `/api/admin/users/${userId}/role`,
    { role },
  );
}

export function updateUserCapabilities(userId: string, extra_capabilities: CapabilityKey[]) {
  return callWorker<{ user_id: string; extra_capabilities: CapabilityKey[] }>(
    'PATCH',
    `/api/admin/users/${userId}/capabilities`,
    { extra_capabilities },
  );
}

export function updateUserLocations(
  userId: string,
  location_ids: string[],
  primary_location_id: string | null,
) {
  return callWorker<{ user_id: string; location_ids: string[] }>(
    'PATCH',
    `/api/admin/users/${userId}/locations`,
    { location_ids, primary_location_id },
  );
}

export function setUserActive(userId: string, is_active: boolean) {
  return callWorker<{ user_id: string; is_active: boolean }>(
    'PATCH',
    `/api/admin/users/${userId}/active`,
    { is_active },
  );
}

export function deleteUser(userId: string) {
  return callWorker<{ user_id: string; deleted: true }>(
    'DELETE',
    `/api/admin/users/${userId}`,
  );
}

// Dernière connexion par user (auth.users.last_sign_in_at via API admin).
export type LastSignInMap = Record<string, { last_sign_in_at: string | null; created_at: string | null }>;
export function fetchLastSignIns() {
  return callWorker<{ ok: true; users: LastSignInMap }>('GET', '/api/admin/users/last-sign-in')
    .then((r) => r.users);
}

// ─── PATCH /api/admin/users/:id — édition profil complet ───────────────────

export type UpdateStaffPayload = {
  first_name?: string;
  last_name?: string;
  display_name?: string | null;
  hire_date?: string | null;
  contract_type?: 'variable' | 'fixed_39' | 'fixed_35' | null;
  contract_hours_per_week?: number | null;
  evening_surcharge_enabled?: boolean;
  sunday_surcharge_enabled?: boolean;
  is_supervisor?: boolean;
  cycle_weeks?: number;
  cycle_reference_date?: string | null;
  default_position_id?: string | null;
  pole?: 'vente' | 'cuisine' | 'bureau' | null; // pôle métier (v0.116)
  display_color?: string;
  phone?: string | null;                     // téléphone perso
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  emergency_contact_relation?: string | null;
  emergency_contact2_name?: string | null;
  emergency_contact2_phone?: string | null;
  emergency_contact2_relation?: string | null;
  date_of_birth?: string | null;             // v0.32 — date ISO (YYYY-MM-DD)
  social_security_number?: string | null;    // v0.32 — texte (15 chars FR)
  address?: string | null;                   // v0.32 — adresse postale
  medical_treatment?: string | null;         // v0.32 — traitement / allergies
  mutuelle_status?: 'subscribed' | 'dismissed' | null;
  image_rights_signed?: boolean;
};

export function updateStaff(userId: string, payload: UpdateStaffPayload) {
  return callWorker<{ user_id: string; updated_fields: string[] }>(
    'PATCH',
    `/api/admin/users/${userId}`,
    payload,
  );
}

// ─── /api/admin/shifts/generate ────────────────────────────────────────────

export type GenerateShiftsPayload = {
  from_date: string;                  // YYYY-MM-DD (lundi de préférence)
  weeks_count?: number;               // 1..52, default 4
  location_ids?: string[] | null;
  user_ids?: string[] | null;
  status?: 'draft' | 'published';     // default 'draft'
};

export type GenerateShiftsResponse = {
  result: {
    shifts_created: number;
    shifts_skipped: number;
    details: Array<{ user_id: string; date: string; reason: string; position_id: string }>;
  } | null;
};

export function generateShifts(payload: GenerateShiftsPayload) {
  return callWorker<GenerateShiftsResponse>('POST', '/api/admin/shifts/generate', payload);
}

// ─── /api/admin/shifts/publish-week ────────────────────────────────────────

export type PublishWeekPayload = {
  from_date: string;
  to_date: string;
  location_ids?: string[] | null;
};

export function publishWeek(payload: PublishWeekPayload) {
  return callWorker<{ shifts_published: number }>(
    'POST',
    '/api/admin/shifts/publish-week',
    payload,
  );
}

// ─── /api/admin/hygiene/traceability-check ─────────────────────────────────

export type OverdueKind = 'traceability' | 'temperature';

export type OverduePvDto = {
  kind: OverdueKind;
  location_id: string;
  location_name: string;
  last_entry_at: string | null;
  days_since: number | null;
};

export type TraceabilityCheckResult = {
  ok: boolean;
  sent: boolean;
  overdue: OverduePvDto[];
  recipients?: string[];
  reason?: 'no_overdue' | 'no_recipients';
};

/**
 * Déclenche à la demande la vérification "PV en retard de traçabilité" et
 * l'envoi de l'email (même logique que le cron quotidien). Lève WorkerError
 * si la route échoue (403 sans cap, 500 si erreur d'envoi / config).
 */
export function checkTraceabilityAlert() {
  return callWorker<TraceabilityCheckResult>('POST', '/api/admin/hygiene/traceability-check');
}

// ─── /api/admin/hygiene/temp-alert ─────────────────────────────────────────
// Alerte immédiate « relevé de température non conforme » : crée la/les
// ligne(s) de supervision + envoie l'email aux destinataires hygiène.

export type TempAlertReadingDto = {
  equipment: string;
  value: number | null;
  targetMin: number | null;
  targetMax: number | null;
  corrective: string | null;
};

export type SendTempAlertPayload = {
  locationId: string;
  locationName: string;
  readings: TempAlertReadingDto[];
  reportedBy?: string | null;
};

export type SendTempAlertResult = {
  ok: boolean;
  alertsCreated: number;
  emailSent: boolean;
  recipients: string[];
};

export function sendTempAlert(payload: SendTempAlertPayload) {
  return callWorker<SendTempAlertResult>('POST', '/api/admin/hygiene/temp-alert', payload);
}

// ─── /api/admin/hygiene/intervention-request ───────────────────────────────
// Notifie par email une demande d'intervention (la tâche to-do MM est créée
// côté front, via mm-task-client).

export type InterventionRequestPayload = {
  locationName: string;
  title: string;
  description?: string | null;
  priority?: 'high' | 'med' | 'low';
  assigneeName?: string | null;
  reportedBy?: string | null;
};

export function sendInterventionRequestEmail(payload: InterventionRequestPayload) {
  return callWorker<{ ok: boolean; emailSent: boolean; recipients: string[] }>(
    'POST', '/api/admin/hygiene/intervention-request', payload,
  );
}

// ─── /api/cash/ecart-alert ─────────────────────────────────────────────────
// Alerte immédiate « écart de caisse » : envoyée à la validation d'une journée
// dont l'écart dépasse le seuil (severity ≠ ok). Best-effort côté front.

export type CashEcartAlertPayload = {
  locationId: string;
  locationName: string;
  businessDate: string;
  ecart: number;
  severity: 'to_check' | 'high' | 'critical';
  theorique: number;
  comptageReel: number;
  caTtc: number;
  especes: number;
  reportedBy?: string | null;
};

export function sendCashEcartAlert(payload: CashEcartAlertPayload) {
  return callWorker<{ ok: boolean; emailSent: boolean; recipients: string[] }>(
    'POST', '/api/cash/ecart-alert', payload,
  );
}

// Aperçu à la demande « dépôt manquant » (même calcul que le cron). N'envoie AUCUN
// email : l'alerte ne part que dans le récap consolidé du matin. Réservé cash.manage.
// Renvoie les PV en retard pour affichage à l'écran.
export type MissingDepositCheckResult = {
  ok: boolean;
  disabled?: boolean;
  days: number;
  total?: number;
  overdue: { location_name: string; sorties: number; deposits: number; undeposited: number }[];
};

export function runMissingDepositCheck() {
  return callWorker<MissingDepositCheckResult>('POST', '/api/cash/missing-deposit-check');
}

// ─── /api/auth (PUBLIC — mot de passe oublié, sans session) ──────────────────
// Envoie un lien de réinitialisation à l'email du salarié (via Resend). Réponse
// TOUJOURS { ok: true } (aucune énumération de comptes) → on ne révèle jamais si
// l'email existe. Best-effort côté UI : on affiche un message neutre.
export function requestPasswordReset(email: string) {
  return callWorker<{ ok: boolean }>('POST', '/api/auth/forgot-password', { email });
}

// ─── /api/newsletter (PUBLIC — inscription client, sans session) ──────────────
export type NewsletterSubscribePayload = {
  brand: 'pitchoun' | 'npa';
  email: string;
  name?: string;
  pv?: string;             // slug du PV (QR) ; absent = choix sur le formulaire site
  eventCatering?: boolean; // Pitchoun « traiteur événementiel »
  consent?: boolean;
  hp?: string;             // honeypot
};

export function subscribeNewsletter(payload: NewsletterSubscribePayload) {
  return callWorker<{ ok: boolean; duplicate?: boolean; error?: string }>(
    'POST', '/api/newsletter/subscribe', payload,
  );
}

// Liste publique des PV d'une enseigne (sélecteur du formulaire site).
export function getNewsletterLocations(brand: 'pitchoun' | 'npa') {
  return callWorker<{ ok: boolean; locations: { slug: string; name: string }[] }>(
    'GET', `/api/newsletter/locations?brand=${brand}`,
  );
}

// ─── /api/candidatures (PUBLIC — dépôt de CV « emploi », sans session) ────────
export type CandidatureSubmitPayload = {
  position: string;
  file: File;
  name?: string;
  phone?: string;
  email?: string;
  note?: string;
  hp?: string; // honeypot
};

// Envoi multipart (CV joint) direct vers le Worker public — pas de bearer, pas
// de Content-Type manuel (le navigateur pose la boundary du FormData).
export async function submitCandidature(
  p: CandidatureSubmitPayload,
): Promise<{ ok: boolean; error?: string }> {
  const fd = new FormData();
  fd.set('position', p.position);
  fd.set('cv', p.file);
  if (p.name) fd.set('name', p.name);
  if (p.phone) fd.set('phone', p.phone);
  if (p.email) fd.set('email', p.email);
  if (p.note) fd.set('note', p.note);
  if (p.hp) fd.set('hp', p.hp);
  const res = await fetch(`${WORKER_URL}/api/candidatures`, { method: 'POST', body: fd });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  return { ok: res.ok && data.ok === true, error: data.error };
}

// Déclenche à la demande la vérification « comptage manquant » (même logique que
// le cron quotidien). Réservé cash.manage.
export type MissingCountCheckResult = {
  ok: boolean;
  sent: boolean;
  days: number;
  overdue: { location_name: string; dates: string[] }[];
  recipients?: string[];
  reason?: string;
};

export function runMissingCountCheck() {
  return callWorker<MissingCountCheckResult>('POST', '/api/cash/missing-count-check');
}

// ─── /api/backups (reporting.read / admin) — état des sauvegardes du Mac ─────
export type BackupJobHealth = {
  job: string;
  label: string | null;
  status: string;
  derived: 'ok' | 'stale' | 'error' | 'pending';
  stale_hours: number | null;
  last_run_at: string | null;
  last_success_at: string | null;
  age_hours: number | null;
  detail: string | null;
  updated_at: string | null;
};

export type BackupsStatus = {
  ok: boolean;
  jobs: BackupJobHealth[];
  problems: BackupJobHealth[];
  healthy: boolean;
};

export function fetchBackups() {
  return callWorker<BackupsStatus>('GET', '/api/backups');
}

// ─── /api/caisse-recon (cash.read) — rapprochement caisse automatique (pilote) ─
export type CaisseReconJour = {
  id: string;
  pv: string;
  jour: string;                      // YYYY-MM-DD
  comptage_especes: number | null;
  comptage_veille: number | null;
  ventes_especes: number | null;
  mouvements: number | null;
  ecart_especes: number | null;
  telecollecte_cb: number | null;
  cb_systeme: number | null;
  ecart_cb: number | null;
  statut: 'ok' | 'a_verifier';
  source: string;
  source_save: string | null;
  updated_at: string | null;
};

export type CaisseReconStatus = {
  ok: boolean;
  seuil: number;                     // seuil d'alerte en €
  pvs: string[];                     // PV disponibles
  jours: CaisseReconJour[];
  a_verifier: number;
};

export function fetchCaisseRecon(pv?: string, limit?: number) {
  const params = new URLSearchParams();
  if (pv) params.set('pv', pv);
  if (limit) params.set('limit', String(limit));
  const q = params.toString();
  return callWorker<CaisseReconStatus>('GET', `/api/caisse-recon${q ? `?${q}` : ''}`);
}

// ─── /api/portage (admin.all) — demandes de portage de repas ─────────────────
export type PortageDemande = {
  id: string;
  created_at: string;
  nom: string | null;
  prenom: string | null;
  telephone: string | null;
  email: string | null;
  adresse: string | null;
  code_postal: string | null;
  ville: string | null;
  nb_repas: number | null;
  formule: string | null;
  jours: string[] | null;
  date_debut: string | null;
  regime: string | null;
  commentaire: string | null;
  statut: 'nouveau' | 'en_cours' | 'traite';
  source: string;
};

export type PortageSettings = {
  alert_recipients: string[];
  email_enabled: boolean;
  todo_enabled: boolean;
  todo_assignee_id: number;
  todo_category: string;
  delivery_days: string[];
};

export function fetchPortage(statut?: string) {
  const q = statut ? `?statut=${encodeURIComponent(statut)}` : '';
  return callWorker<{ ok: true; demandes: PortageDemande[]; nouveaux: number }>(
    'GET', `/api/portage${q}`,
  );
}

export function fetchPortageCount() {
  return callWorker<{ ok: true; nouveaux: number }>('GET', '/api/portage/count');
}

export function updatePortageStatut(id: string, statut: PortageDemande['statut']) {
  return callWorker<{ ok: true }>('PATCH', `/api/portage/${id}`, { statut });
}

export function fetchPortageSettings() {
  return callWorker<{ ok: true; settings: Record<string, unknown> }>('GET', '/api/portage/settings');
}

export function updatePortageSettings(payload: Partial<PortageSettings>) {
  return callWorker<{ ok: true }>('PATCH', '/api/portage/settings', payload);
}
